<template>
  <div class="pb-page pl">
    <PageHeader title="Compare plans" subtitle="What each plan includes. Collecting, reviewing and exporting your feedback work on every plan." />

    <p v-if="!buyer" class="pb-note"><i class="mdi mdi-account-supervisor-outline pb-ico" aria-hidden="true"></i>
      <span>Billing belongs to the workspace owner. Ask {{ ownerEmail || 'them' }} to change the plan.</span></p>

    <SkeletonList v-if="!plans.length" :rows="3" label="Loading plans…" />
    <div v-else class="pb-card pl-card">
      <table class="pl-table">
        <caption class="pb-sr-only">{{ plans.map(item => item.label).join(', ') }}: what each plan includes</caption>
        <thead>
          <tr>
            <th scope="col" class="pl-feature"><span class="pb-sr-only">Feature</span></th>
            <th v-for="item in plans" :key="item.name" scope="col" :class="['pl-plan', { current: item.name === plan }]">
              <span class="pl-name">{{ item.label }}</span>
              <span class="pl-price">{{ item.price }}<small v-if="item.paid"> a month</small></span>
              <span v-if="item.name === plan" class="pb-chip approved pl-yours">Your plan</span>
              <router-link v-else-if="buyer && item.higher" :to="{ path: '/subscribe', query: { plan: item.name } }" class="pb-button sm pl-choose">See {{ item.label }}</router-link>
            </th>
          </tr>
        </thead>
        <tbody v-for="section in sections" :key="section.title">
          <tr class="pl-section"><th :colspan="plans.length + 1" scope="colgroup">{{ section.title }}</th></tr>
          <tr v-for="row in section.rows" :key="row.label">
            <th scope="row" class="pl-feature">{{ row.label }}</th>
            <td v-for="item in plans" :key="item.name" :class="{ current: item.name === plan }">
              <template v-if="row.value(item.limits) === true"><i class="mdi mdi-check pl-yes" aria-hidden="true"></i><span class="pb-sr-only">Included</span></template>
              <template v-else-if="row.value(item.limits) === false"><i class="mdi mdi-minus pl-no" aria-hidden="true"></i><span class="pb-sr-only">Not included</span></template>
              <template v-else>{{ row.value(item.limits) }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="pb-muted pl-foot">Prices in USD before tax, billed monthly. Unlimited means no per-item cap, within the fair-use limits in the Terms.
      Nothing is deleted when you change plans.</p>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue';
import SkeletonList from '../components/SkeletonList.vue';
import workspace from '../mixins/workspace';
import { monthlyPrice, planOrder } from '../lib/plans';

const amount = (value, unit) => (value === null ? 'Unlimited' : String(value) + (unit ? ' ' + unit : ''));

// The PRD 9.2 comparison as this build offers it, every value read from the plan's limits (policy.LIMITS).
const SECTIONS = [
  { title: 'Collect', rows: [
    { label: 'Active collection pages', value: l => amount(l.forms) },
    { label: 'Questions', value: l => (l.custom_questions ? 'Your own' : 'Up to ' + l.preset_questions + ' preset') },
    { label: 'Question templates', value: l => (l.template_library === 'full' ? 'Full library' : 'Basic') },
    { label: 'Private feedback', value: () => 'Unlimited' }
  ] },
  { title: 'Publish', rows: [
    { label: 'Published testimonials', value: l => amount(l.published) },
    { label: 'Active widgets', value: l => amount(l.widgets) },
    { label: 'Widget layouts', value: l => ((l.layouts || []).length > 1 ? 'All ' + l.layouts.length : 'Grid') },
    { label: 'Widget styling', value: l => (l.styling === 'advanced' ? 'Advanced' : 'Basic') },
    { label: 'Hosted walls', value: l => String(l.brands) },
    { label: 'TrustClap credit', value: l => (l.remove_branding ? 'Removable' : 'Shown') },
    { label: 'Quote graphics', value: l => (l.graphics === 'advanced' ? 'All formats, no mark' : 'Basic, with mark') },
    { label: 'Date-range reports', value: l => l.reports === 'advanced' }
  ] },
  { title: 'Team and clients', rows: [
    { label: 'Active brands', value: l => String(l.brands) },
    { label: 'Team members, including the owner', value: l => String(l.seats) },
    { label: 'Team permissions', value: l => (l.seats > 1 ? (l.brand_scoped_seats ? 'By brand' : 'Roles') : false) },
    { label: 'Client approvals', value: l => !!l.client_approvals },
    { label: 'Restricted client reviewers', value: l => !!l.client_reviewers },
    { label: 'Agency name on client pages', value: l => !!l.white_label_approvals },
    { label: 'Reusable templates', value: l => !!l.agency_templates },
    { label: 'Client report links', value: l => !!l.client_reports }
  ] },
  { title: 'On every plan', rows: [
    { label: 'Collection links and QR codes', value: () => true },
    { label: 'Import by paste, CSV or one by one', value: () => true },
    { label: 'CSV export', value: () => true },
    { label: 'Permission records and withdrawal links', value: () => true },
    { label: 'Reports for the last 30 days', value: () => true }
  ] }
];

export default {
  name: 'PlansView',
  components: { PageHeader, SkeletonList },
  mixins: [workspace],
  computed: {
    sections() { return SECTIONS; },
    plans() {
      const order = planOrder(this.planMatrix);
      const mine = order.indexOf(this.plan);
      return order.map((name, index) => ({ name, label: this.planName(name), limits: this.planMatrix[name].limits,
        price: monthlyPrice(this.planMatrix, name), paid: Number(this.planMatrix[name].price_monthly) > 0, higher: index > mine }));
    },
    // Billing is the owner's (a new account without a workspace is its own owner).
    buyer() { return this.isOwner || !this.$store.getters['praiseboard/hasWorkspace']; },
    ownerEmail() { return (this.ws && this.ws.owner_email) || ''; }
  },
  async mounted() { await this.$store.dispatch('praiseboard/ensure').catch(() => null); }
};
</script>

<style scoped>
.pl-card{padding:0;overflow:hidden}
.pl-table{width:100%;border-collapse:collapse;table-layout:fixed;font-size:14.5px}
.pl-table th,.pl-table td{padding:12px 16px;text-align:left;border-bottom:1px solid #EFE9E0;vertical-align:middle}
.pl-feature{width:34%;font-weight:500;color:#1F2328}
.pl-table thead th{vertical-align:top;background:#FBF8F3}
.pl-plan{font-weight:400}
.pl-name{display:block;font:600 19px/1.25 var(--font-display);color:#1F2328}
.pl-price{display:block;margin-top:2px;font:600 15px/1.4 var(--font-body);color:#1F2328}
.pl-price small{font-weight:500;color:#6B635B}
.pl-yours,.pl-choose{margin-top:10px}
.pl-table td{color:#1F2328;font-variant-numeric:tabular-nums}
.pl-table .current{background:#FBF4F0}
.pl-table thead th.current{background:#F7EBE5}
.pl-section th{padding-top:18px;background:#fff;font:700 12.5px/1.3 var(--font-body);letter-spacing:.06em;text-transform:uppercase;color:#963A22}
.pl-yes{color:#2F7D4F;font-size:20px}
.pl-no{color:#8C8378;font-size:20px}
.pl-foot{margin-top:14px;font-size:13.5px}
@media(max-width:700px){
  .pl-table{font-size:13px}
  .pl-table th,.pl-table td{padding:10px 8px}
  .pl-feature{width:36%}
  .pl-name{font-size:16px}
  .pl-price{font-size:13px}
  .pl-price small{display:block}
  .pl-choose{width:100%;padding:6px 4px;white-space:normal}
  .pl-yours{padding:2px 6px;font-size:11.5px;white-space:normal;text-align:center}
}
</style>
